import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Gamepad2, MessageCircle, MousePointerClick, PartyPopper, Layers, Repeat, Camera } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { ScrollReveal } from '../../components/ScrollReveal';
import { GameLiveDemo } from '../../components/GameLiveDemo';

const rodada = [
  { icon: MessageCircle, title: 'Uma situação real', desc: 'Uma cena do cotidiano — comprimentar um amigo, agradecer, pedir ajuda — com foto.' },
  { icon: MousePointerClick, title: 'Escolha entre as opções', desc: 'Várias respostas possíveis, também com foto, das quais só uma é a mais adequada.' },
  { icon: PartyPopper, title: 'Feedback sem punição', desc: 'Acerto vem com celebração; erro vem neutro, sempre incentivando tentar de novo.' },
];

const stats = [
  { icon: Layers, value: '9', label: 'jogos diferentes' },
  { icon: Repeat, value: 'Várias', label: 'rodadas por jogo' },
  { icon: Camera, value: 'Fotos reais', label: 'em cada opção de resposta' },
];

const JogosPage = () => {
  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Hero */}
      <section className="relative pt-16 pb-14 md:pt-20 md:pb-16 bg-background overflow-hidden">
        <div
          className="absolute top-8 left-[8%] w-40 h-40 rounded-full opacity-25 blur-3xl pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgb(var(--warm)) 0%, transparent 70%)' }}
          aria-hidden="true"
        />
        <div
          className="absolute top-1/3 right-[6%] w-56 h-56 rounded-full opacity-25 blur-3xl pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgb(var(--primary)) 0%, transparent 70%)' }}
          aria-hidden="true"
        />

        <div className="relative container mx-auto px-4">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors mb-6">
            <ArrowLeft className="w-4 h-4" />
            Início
          </Link>

          <div className="inline-flex items-center gap-2 bg-warm/10 dark:bg-warm/15 px-3 py-1.5 rounded-full text-sm font-semibold text-foreground mb-5">
            <Gamepad2 className="w-4 h-4 text-warm-ink dark:text-warm" />
            Jogos Sociais
          </div>

          <h1 className="text-4xl md:text-6xl font-bold text-foreground max-w-3xl leading-[1.1]">
            Aprendendo <span className="text-warm-ink dark:text-warm">brincando</span>
          </h1>
          <p className="font-body text-lg md:text-xl text-muted-foreground mt-4 max-w-2xl">
            9 jogos com situações do dia a dia para treinar habilidades sociais, raciocínio
            e inteligência emocional.
          </p>
        </div>
      </section>

      {/* Demo + como funciona a rodada, espelhado (demo à esquerda) */}
      <ScrollReveal>
        <section className="py-16 md:py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center max-w-5xl mx-auto">
              <div className="w-full max-w-sm mx-auto">
                <GameLiveDemo />
              </div>

              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-6">
                  Como cada rodada funciona
                </h2>
                <div className="space-y-4">
                  {rodada.map((item, i) => (
                    <div key={item.title} className="flex items-start gap-3">
                      <span className="w-9 h-9 rounded-lg bg-background flex items-center justify-center flex-shrink-0 shadow-sm relative">
                        <item.icon className="w-4 h-4 text-warm-ink dark:text-warm" />
                        <span className="absolute -top-1.5 -left-1.5 w-4 h-4 rounded-full bg-warm text-white text-[10px] font-bold flex items-center justify-center">
                          {i + 1}
                        </span>
                      </span>
                      <div>
                        <h3 className="font-semibold text-foreground text-sm">{item.title}</h3>
                        <p className="text-sm text-muted-foreground mt-0.5">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* Números do módulo */}
      <ScrollReveal>
        <section className="py-16 md:py-20 bg-background">
          <div className="container mx-auto px-4">
            <div className="grid sm:grid-cols-3 gap-5 max-w-4xl mx-auto">
              {stats.map((stat) => (
                <div key={stat.label} className="rounded-2xl bg-warm/[0.06] dark:bg-warm/[0.11] px-6 py-8 text-center">
                  <stat.icon className="w-6 h-6 text-warm-ink dark:text-warm mx-auto mb-3" />
                  <div className="text-2xl font-bold text-foreground">{stat.value}</div>
                  <div className="text-sm text-muted-foreground mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
            <p className="text-center text-sm text-muted-foreground max-w-lg mx-auto mt-8">
              O progresso de cada jogo — taxa de acerto, tempo médio de resposta — fica
              registrado e alimenta o painel de Acompanhamento de Progresso.
            </p>
          </div>
        </section>
      </ScrollReveal>

      {/* CTA final */}
      <section className="py-16 bg-warm/5">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
            Quer ver os outros recursos?
          </h2>
          <Link
            to="/recursos/musica"
            className="inline-flex items-center gap-1.5 font-sans font-bold text-[15px] px-7 py-3.5 rounded-full bg-primary text-white hover:bg-primary/90 transition-transform hover:-translate-y-0.5"
          >
            Conhecer a Música Terapêutica
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default JogosPage;
